import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/feature/MovieCard";
import ArticleRow from "@/components/feature/ArticleRow";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { getHotMovies, paginate, searchArticles, searchMovies, totalPages } from "@/lib/media";

type TabKey = "movie" | "article";

const MOVIE_PAGE_SIZE = 8;
const ARTICLE_PAGE_SIZE = 5;

const suggestions = ["悬疑", "科幻", "雾都谜案", "治愈", "动画", "韩剧", "影评"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q")?.trim() ?? "";
  const [input, setInput] = useState(query);
  const [tab, setTab] = useState<TabKey>("movie");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setInput(query);
    setPage(1);
    setTab("movie");
  }, [query]);

  const movieResult = useMemo(() => searchMovies(query), [query]);
  const articleResult = useMemo(() => searchArticles(query), [query]);

  const maxMoviePage = totalPages(movieResult.length, MOVIE_PAGE_SIZE);
  const maxArticlePage = totalPages(articleResult.length, ARTICLE_PAGE_SIZE);
  const currentMoviePage = Math.min(page, maxMoviePage);
  const currentArticlePage = Math.min(page, maxArticlePage);

  const moviesPage = paginate(movieResult, currentMoviePage, MOVIE_PAGE_SIZE);
  const articlesPage = paginate(articleResult, currentArticlePage, ARTICLE_PAGE_SIZE);

  useDocumentMeta({
    title: query ? `搜索「${query}」 - 色虎全站搜索` : "全站搜索 - 色虎影视",
    description: query
      ? `色虎全站搜索「${query}」的结果，共找到 ${movieResult.length} 部影视作品与 ${articleResult.length} 篇影视资讯。`
      : "色虎全站搜索支持同时检索影视作品与影视资讯，输入片名、导演、演员或关键词即可快速找到内容。",
    keywords: "色虎搜索,影视搜索,影片检索,资讯搜索",
  });

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    if (!value) return;
    setSearchParams({ q: value });
  };

  const runSuggestion = (value: string) => {
    setInput(value);
    setSearchParams({ q: value });
  };

  const activeResultCount = tab === "movie" ? movieResult.length : articleResult.length;

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="portal-container py-8 md:py-10">
          <nav aria-label="面包屑导航" className="flex items-center gap-1.5 text-xs text-foreground-400">
            <Link to="/" className="hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
            <span className="text-foreground-600">全站搜索</span>
          </nav>

          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            {query ? (
              <>
                搜索「<span className="text-accent-600">{query}</span>」
              </>
            ) : (
              "全站搜索"
            )}
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            {query
              ? `共找到 ${movieResult.length} 部影视作品与 ${articleResult.length} 篇影视资讯（演示数据，前端即时过滤）`
              : "输入片名、导演、演员或关键词，同时检索影视作品与影视资讯。"}
          </p>

          <form onSubmit={submit} role="search" className="mt-5 flex flex-col gap-2 sm:flex-row">
            <div className="flex h-11 flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 focus-within:border-primary-400">
              <i className="ri-search-line text-base text-foreground-400" aria-hidden="true" />
              <input
                type="search"
                name="q"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="搜索影片名称、导演、演员或资讯关键词"
                aria-label="搜索关键词"
                className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              />
              {input && (
                <button
                  type="button"
                  aria-label="清空关键词"
                  onClick={() => setInput("")}
                  className="flex h-6 w-6 cursor-pointer items-center justify-center rounded text-foreground-400 hover:text-foreground-700"
                >
                  <i className="ri-close-line" aria-hidden="true" />
                </button>
              )}
            </div>
            <button type="submit" className="btn-primary h-11 px-6">
              搜索
            </button>
          </form>

          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <span>热门搜索：</span>
            {suggestions.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => runSuggestion(item)}
                className="cursor-pointer rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-foreground-600 transition-colors hover:border-accent-300 hover:text-accent-700"
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="portal-container mt-8">
        {!query ? (
          <div className="space-y-8">
            <EmptyState
              icon="ri-search-line"
              title="还没有输入关键词"
              description="可以搜索片名（如「雾都谜案」）、类型（如「悬疑」）或导演与演员姓名。"
            />
            <section>
              <div className="mb-4 flex items-center justify-between border-b border-background-200 pb-3">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <span className="h-4 w-1 rounded-full bg-accent-500" aria-hidden="true" />
                  先看看大家都在找
                </h2>
                <Link to="/category/movie" className="text-xs font-medium text-primary-700 hover:text-accent-600">
                  全部热门
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
                {getHotMovies(5).map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </section>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 border-b border-background-200 pb-3">
                <button
                  type="button"
                  onClick={() => {
                    setTab("movie");
                    setPage(1);
                  }}
                  className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    tab === "movie"
                      ? "bg-primary-500 font-medium text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  影视作品
                  <span className={`text-xs ${tab === "movie" ? "text-primary-200" : "text-foreground-400"}`}>
                    {movieResult.length}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTab("article");
                    setPage(1);
                  }}
                  className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    tab === "article"
                      ? "bg-primary-500 font-medium text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  影视资讯
                  <span className={`text-xs ${tab === "article" ? "text-primary-200" : "text-foreground-400"}`}>
                    {articleResult.length}
                  </span>
                </button>
              </div>

              <div className="mt-5">
                {activeResultCount === 0 ? (
                  <EmptyState
                    icon="ri-emotion-sad-line"
                    title={`没有找到与「${query}」相关的内容`}
                    description="试试更短的关键词，或者换一个类型词，例如「悬疑」「科幻」「治愈」。"
                  />
                ) : tab === "movie" ? (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
                    {moviesPage.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg border border-background-200 bg-background-50 px-4 py-1 md:px-5">
                    {articlesPage.map((article) => (
                      <ArticleRow key={article.id} article={article} />
                    ))}
                  </div>
                )}
              </div>

              {activeResultCount > 0 && (
                <Pagination
                  page={tab === "movie" ? currentMoviePage : currentArticlePage}
                  total={tab === "movie" ? maxMoviePage : maxArticlePage}
                  onChange={setPage}
                />
              )}

              <div className="mt-8 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
                <p className="text-xs leading-6 text-foreground-500">
                  <i className="ri-information-line mr-1" aria-hidden="true" />
                  搜索结果基于演示数据的标题、导演、演员、类型与标签匹配，不连接真实后端搜索服务。
                </p>
              </div>
            </div>

            <aside className="space-y-6">
              <section className="card-surface p-4">
                <h2 className="flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-sm font-semibold text-foreground-950">
                  <i className="ri-lightbulb-line text-accent-500" aria-hidden="true" />
                  换个方式找
                </h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[
                    { label: "电影", to: "/category/movie" },
                    { label: "电视剧", to: "/category/tv" },
                    { label: "综艺", to: "/category/variety" },
                    { label: "动画", to: "/category/anime" },
                  ].map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="rounded-md border border-background-200 bg-background-100 px-2.5 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                    >
                      {item.label}分类
                    </Link>
                  ))}
                </div>
              </section>

              <section className="card-surface p-4">
                <h2 className="flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-sm font-semibold text-foreground-950">
                  <i className="ri-fire-line text-accent-500" aria-hidden="true" />
                  热门影片
                </h2>
                <ol className="mt-1">
                  {getHotMovies(4).map((movie, index) => (
                    <li key={movie.id} className="border-b border-background-100 last:border-b-0">
                      <Link to={`/detail/${movie.id}`} className="group flex items-center gap-3 py-3">
                        <span className={`font-heading text-sm font-bold ${index < 3 ? "text-accent-600" : "text-foreground-300"}`}>
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm text-foreground-800 group-hover:text-accent-600">{movie.title}</p>
                          <p className="mt-0.5 text-[11px] text-foreground-400">{movie.year} · {movie.region}</p>
                        </div>
                        <span className="text-xs font-medium text-primary-700">{movie.rating.toFixed(1)}</span>
                      </Link>
                    </li>
                  ))}
                </ol>
              </section>

              <section className="rounded-lg bg-primary-500 p-5 text-background-50">
                <h2 className="font-heading text-base font-bold">影视资讯也别错过</h2>
                <p className="mt-2 text-xs leading-6 text-primary-100">
                  除了影片信息，色虎每天更新行业报道、深度影评与幕后专题。
                </p>
                <Link
                  to="/news"
                  className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-3 py-1.5 text-xs font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                >
                  前往资讯频道
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </section>
            </aside>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}